import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { paymentsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Crown, Check, Loader2, CreditCard, XCircle, RefreshCw, Receipt, ExternalLink } from 'lucide-react';

const Subscription = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [managing, setManaging] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      checkSubscription();
    } else {
      setCheckingStatus(false);
    }
  }, [isAuthenticated]);

  const checkSubscription = async () => {
    try {
      const response = await paymentsAPI.getSubscriptionStatus();
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Error checking subscription:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleSubscribe = async () => {
    if (!isAuthenticated) {
      navigate('/signin');
      return;
    }

    setLoading(true);
    try {
      const response = await paymentsAPI.createSubscriptionCheckout();
      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        console.error('No checkout URL received:', response.data);
        alert('Failed to create checkout session. Please try again.');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Failed to start checkout: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Cancel your $1.99/month subscription? You'll keep premium access until the end of your current billing period.")) {
      return;
    }
    setManaging(true);
    try {
      await paymentsAPI.cancelSubscription();
      await checkSubscription();
    } catch (error) {
      alert(error.response?.data?.detail || 'Could not cancel subscription.');
    } finally {
      setManaging(false);
    }
  };

  const handleResume = async () => {
    setManaging(true);
    try {
      await paymentsAPI.resumeSubscription();
      await checkSubscription();
    } catch (error) {
      alert(error.response?.data?.detail || 'Could not resume subscription.');
    } finally {
      setManaging(false);
    }
  };

  const handleManageBilling = async () => {
    setManaging(true);
    try {
      const res = await paymentsAPI.openBillingPortal();
      const url = res?.data?.portal_url;
      if (url) {
        window.location.href = url;
      } else {
        alert('Could not open billing portal.');
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Could not open billing portal.');
    } finally {
      setManaging(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-electric animate-spin" />
      </div>
    );
  }

  const features = [
    'Unlimited access to all movies',
    'Unlimited access to all TV shows',
    'Ad-free streaming experience',
    'High quality video playback',
    'Watch on multiple devices',
    'Download for offline viewing',
    'Early access to new releases',
    'Exclusive member content'
  ];

  return (
    <div className="min-h-screen py-12 px-4" data-testid="subscription-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <Crown className="h-16 w-16 text-electric mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Pulse Media Premium
          </h1>
          <p className="text-xl text-foreground/70 max-w-2xl mx-auto">
            Unlock unlimited access to our entire library of movies, TV shows, and music videos.
          </p>
        </div>

        {/* Already Premium */}
        {subscriptionStatus?.is_premium && (
          <div className="max-w-lg mx-auto mb-12 p-6 rounded-2xl bg-electric/20 border border-electric/50 text-center" data-testid="premium-status-card">
            <Check className="h-12 w-12 text-electric mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">You're a Premium Member!</h2>
            {subscriptionStatus.cancel_at_period_end ? (
              <>
                <p className="text-foreground/70 mb-4">
                  Your subscription is cancelled but you keep premium access until{' '}
                  <span className="text-electric font-semibold">
                    {new Date(subscriptionStatus.expires_at).toLocaleDateString()}
                  </span>.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <button
                    onClick={handleResume}
                    disabled={managing}
                    className="inline-flex items-center space-x-2 px-5 py-2 rounded-xl bg-electric text-background font-semibold hover:bg-electric/90 disabled:opacity-50"
                    data-testid="resume-subscription-btn"
                  >
                    {managing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    <span>Resume Subscription</span>
                  </button>
                  <button
                    onClick={handleManageBilling}
                    disabled={managing}
                    className="inline-flex items-center space-x-2 px-5 py-2 rounded-xl bg-white/10 border border-white/20 text-foreground hover:bg-white/15 disabled:opacity-50"
                    data-testid="manage-billing-btn-cancelled"
                  >
                    {managing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Receipt className="h-4 w-4" />}
                    <span>Manage Billing</span>
                    <ExternalLink className="h-3 w-3 opacity-70" />
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-foreground/70 mb-4">
                  Next billing date:{' '}
                  <span className="text-electric font-semibold">
                    {subscriptionStatus.expires_at ? new Date(subscriptionStatus.expires_at).toLocaleDateString() : '—'}
                  </span>
                </p>
                {subscriptionStatus.has_stripe_subscription && (
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    <button
                      onClick={handleManageBilling}
                      disabled={managing}
                      className="inline-flex items-center space-x-2 px-5 py-2 rounded-xl bg-electric text-background font-semibold hover:bg-electric/90 disabled:opacity-50"
                      data-testid="manage-billing-btn"
                    >
                      {managing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Receipt className="h-4 w-4" />}
                      <span>Manage Billing</span>
                      <ExternalLink className="h-3 w-3 opacity-70" />
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={managing}
                      className="inline-flex items-center space-x-2 px-5 py-2 rounded-xl bg-white/10 border border-white/20 text-foreground hover:bg-white/15 disabled:opacity-50"
                      data-testid="cancel-subscription-btn"
                    >
                      {managing ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                      <span>Cancel Subscription</span>
                    </button>
                  </div>
                )}
                <p className="text-xs text-foreground/50 mt-4">
                  Manage Billing opens Stripe's secure portal — update your card, download past invoices, or cancel anytime.
                </p>
              </>
            )}
          </div>
        )}

        {/* Pricing Card */}
        {!subscriptionStatus?.is_premium && (
          <div className="max-w-lg mx-auto">
            <div className="bg-card/80 backdrop-blur-3xl border border-white/20 rounded-3xl overflow-hidden shadow-2xl shadow-electric/20" style={{ backdropFilter: 'blur(40px) saturate(180%)' }}>
              {/* Price Header */}
              <div className="bg-electric/10 border-b border-electric/20 p-6 text-center">
                <span className="text-sm font-medium text-electric uppercase tracking-wider">Monthly Plan</span>
                <div className="mt-4 flex items-baseline justify-center">
                  <span className="text-5xl font-bold text-foreground">$1.99</span>
                  <span className="text-foreground/60 ml-2">/month</span>
                </div>
              </div>

              {/* Features */}
              <div className="p-6 space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-5 h-5 rounded-full bg-electric/20 flex items-center justify-center flex-shrink-0">
                      <Check className="h-3 w-3 text-electric" />
                    </div>
                    <span className="text-foreground/90">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="p-6 border-t border-white/10 space-y-3">
                <button
                  onClick={handleSubscribe}
                  disabled={loading}
                  className="w-full flex items-center justify-center space-x-2 py-4 rounded-xl bg-electric text-background font-semibold hover:bg-electric/90 transition-all disabled:opacity-50"
                  data-testid="stripe-subscribe-button"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <CreditCard className="h-5 w-5" />
                      <span>Subscribe with Card</span>
                    </>
                  )}
                </button>
              </div>

              {/* Recurring billing disclosure */}
              <div className="px-6 pb-6">
                <p className="text-xs text-center text-foreground/50">
                  Billed <span className="text-foreground/80">$1.99 USD every month</span> until you cancel.
                  You can cancel anytime from this page — no fees, no questions.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Pay-Per-View Note */}
        <div className="mt-12 text-center">
          <p className="text-foreground/60">
            Don't want a subscription? Some content is available for{' '}
            <span className="text-electric font-semibold">$4.99 Pay-Per-View</span> with 7-day access.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
